import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Account, Workflow, Recipe, Task } from './types'
import { SEED_ACCOUNTS, SEED_WORKFLOWS, SEED_RECIPES } from './data'

interface OpsCenterState {
  accounts: Account[]
  workflows: Workflow[]
  recipes: Recipe[]
  selectedAccountId: string | null
  selectedWorkflowId: string | null
  selectedRecipeId: string | null
  selectedNodeId: string | null

  selectAccount: (id: string | null) => void
  selectWorkflow: (id: string | null) => void
  selectRecipe: (id: string | null) => void
  selectNode: (id: string | null) => void

  addAccount: (name: string) => void
  renameAccount: (id: string, name: string) => void
  deleteAccount: (id: string) => void

  addWorkflow: (accountId: string, wf: Workflow) => void
  updateWorkflow: (id: string, patch: Partial<Workflow>) => void
  updateTasks: (id: string, tasks: Task[]) => void
  deleteWorkflow: (id: string) => void

  addRecipe: (name: string) => void
  updateRecipe: (id: string, patch: Partial<Recipe>) => void
}

const uid = (p: string) => `${p}_${Math.random().toString(36).slice(2, 9)}`

export const useOpsCenterStore = create<OpsCenterState>()(
  persist(
    (set) => ({
      accounts: SEED_ACCOUNTS,
      workflows: SEED_WORKFLOWS,
      recipes: SEED_RECIPES,
      selectedAccountId: null,
      selectedWorkflowId: null,
      selectedRecipeId: null,
      selectedNodeId: null,

      selectAccount: (id) => set({ selectedAccountId: id, selectedWorkflowId: null, selectedNodeId: null }),
      selectWorkflow: (id) => set({ selectedWorkflowId: id, selectedRecipeId: null, selectedNodeId: null }),
      selectRecipe: (id) => set({ selectedRecipeId: id, selectedWorkflowId: null, selectedNodeId: null }),
      selectNode: (id) => set({ selectedNodeId: id }),

      addAccount: (name) => {
        const now = new Date().toISOString()
        set((s) => ({ accounts: [...s.accounts, { id: uid('acc'), name, workflowIds: [], createdAt: now, updatedAt: now }] }))
      },
      renameAccount: (id, name) => set((s) => ({ accounts: s.accounts.map((a) => a.id === id ? { ...a, name } : a) })),
      deleteAccount: (id) => set((s) => ({
        accounts: s.accounts.filter((a) => a.id !== id),
        workflows: s.workflows.filter((w) => w.accountId !== id),
      })),

      addWorkflow: (accountId, wf) => set((s) => ({
        workflows: [...s.workflows, wf],
        accounts: s.accounts.map((a) => a.id === accountId ? { ...a, workflowIds: [...a.workflowIds, wf.id] } : a),
      })),
      updateWorkflow: (id, patch) => set((s) => ({
        workflows: s.workflows.map((w) => w.id === id ? { ...w, ...patch, updatedAt: new Date().toISOString() } : w),
      })),
      updateTasks: (id, tasks) => set((s) => ({
        workflows: s.workflows.map((w) => w.id === id ? { ...w, tasks, updatedAt: new Date().toISOString() } : w),
      })),
      deleteWorkflow: (id) => set((s) => ({
        workflows: s.workflows.filter((w) => w.id !== id),
        accounts: s.accounts.map((a) => ({ ...a, workflowIds: a.workflowIds.filter((w) => w !== id) })),
      })),

      addRecipe: (name) => {
        const now = new Date().toISOString()
        set((s) => ({
          recipes: [...s.recipes, {
            id: uid('recipe'), name: name || 'new_recipe', longName: name || 'New Recipe',
            checkType: ['blacklist', 'two_plus', 'id', 'pep', 'idvalidate'],
            checkOrder: 'blacklist, !id, ent, idv, aml', mkyc: false, createdAt: now, updatedAt: now,
          }],
        }))
      },
      updateRecipe: (id, patch) => set((s) => ({
        recipes: s.recipes.map((r) => r.id === id ? { ...r, ...patch, updatedAt: new Date().toISOString() } : r),
      })),
    }),
    // Bump `version` whenever the seed data changes so the new seeds load over
    // any previously-persisted state (mockup demo data, safe to reset).
    { name: 'ops-center-mockup', version: 3 },
  ),
)
